import React, { useState } from 'react';
import SEO from '../components/SEO';
import { Mail, MessageSquare, MapPin, Send, HelpCircle } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';

export default function ContactUs() {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all fields.');
      return;
    }
    setLoading(true);
    try {
      await api.post('/contact', formData);
      toast.success('Thank you for contacting Pushpanjali! Your message has been received.');
      setFormData({ name: '', email: '', message: '' });
    } catch (err) {
      console.error(err);
      toast.error('Failed to submit message. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bgmain pb-16 md:pb-24 pt-6">
      <SEO 
        title="Contact Pushpanjali Noida | Catering & Order Queries"
        description="Get in touch with Pushpanjali cloud kitchen in Noida. Contact us for food orders, tiffin service subscriptions, catering inquiries, and custom orders."
        keywords="contact pushpanjali, tiffin service queries noida, home food catering noida, cloud kitchen phone"
      />
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        {/* Banner */}
        <div className="relative rounded-3xl overflow-hidden bg-gradient-primary text-white p-8 md:p-12 mb-8 shadow-warm grain">
          <div className="relative z-10">
            <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur px-3 py-1 rounded-full mb-3 border border-white/10">
              <HelpCircle className="w-4 h-4 text-golden" />
              <span className="text-xs font-heading font-medium tracking-wide">SUPPORT CHANNELS</span>
            </div>
            <h1 className="font-heading font-extrabold text-3xl md:text-4xl text-white">Contact Us</h1>
            <p className="text-white/80 font-body text-sm mt-3 max-w-xl">
              Have questions about your order, special food catering requests, or just want to tell us how much you loved the biryani? We are here to help.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-5 gap-8">
          {/* Contact details cards */}
          <div className="md:col-span-2 space-y-6">
            {/* Email card */}
            <div className="bg-white p-6 rounded-3xl border border-borderbeige shadow-card flex items-start gap-4">
              <div className="w-12 h-12 rounded-2xl bg-maroon/5 flex items-center justify-center text-maroon shrink-0">
                <Mail className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-lg text-textprimary">Email Support</h3>
                <p className="text-textsecondary text-sm font-body mt-1">Send us an email and we'll reply within 24 hours.</p>
                <a href="mailto:contact@pushpanjali.cloud" className="text-maroon font-semibold text-sm mt-2 block hover:underline">
                  contact@pushpanjali.cloud
                </a>
              </div>
            </div>

            {/* WhatsApp support card */}
            <div className="bg-white p-6 rounded-3xl border border-borderbeige shadow-card flex items-start gap-4">
              <div className="w-12 h-12 rounded-2xl bg-fresh/10 flex items-center justify-center text-fresh shrink-0">
                <MessageSquare className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-lg text-textprimary">WhatsApp Support</h3>
                <p className="text-textsecondary text-sm font-body mt-1">Get fast help directly on WhatsApp for live order updates.</p>
                <a 
                  href="https://wa.me/919999999999" // Replace placeholder or use standard WhatsApp link
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="inline-flex items-center gap-1.5 text-fresh font-semibold text-sm mt-2 hover:underline"
                >
                  Chat with Us
                </a>
              </div>
            </div>

            {/* Service area card */}
            <div className="bg-white p-6 rounded-3xl border border-borderbeige shadow-card flex items-start gap-4">
              <div className="w-12 h-12 rounded-2xl bg-saffron/10 flex items-center justify-center text-saffron shrink-0">
                <MapPin className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-lg text-textprimary">Our Kitchen Location</h3>
                <p className="text-textsecondary text-sm font-body mt-1">Operating out of Noida, UP.</p>
                <p className="text-textprimary text-sm font-semibold mt-2">Serving: Selected sectors in Noida</p>
              </div>
            </div>
          </div>

          {/* Contact form card */}
          <div className="md:col-span-3">
            <div className="bg-white rounded-3xl border border-borderbeige shadow-card p-6 md:p-8">
              <h2 className="font-heading font-bold text-xl text-textprimary mb-4">Send a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-xs font-heading font-bold text-textsecondary uppercase tracking-wider mb-2">
                    Your Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 rounded-2xl bg-bgmain border border-borderbeige focus:outline-none focus:border-maroon text-textprimary text-sm font-body transition-colors"
                    placeholder="Enter your name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-xs font-heading font-bold text-textsecondary uppercase tracking-wider mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 rounded-2xl bg-bgmain border border-borderbeige focus:outline-none focus:border-maroon text-textprimary text-sm font-body transition-colors"
                    placeholder="name@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-xs font-heading font-bold text-textsecondary uppercase tracking-wider mb-2">
                    Message
                  </label>
                  <textarea
                    id="message"
                    rows="4"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full px-4 py-3 rounded-2xl bg-bgmain border border-borderbeige focus:outline-none focus:border-maroon text-textprimary text-sm font-body transition-colors resize-none"
                    placeholder="How can we help you?"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 bg-maroon hover:bg-maroon-dark text-white font-heading font-bold px-6 py-3.5 rounded-2xl shadow-warm transition-all disabled:opacity-50"
                >
                  {loading ? 'Sending...' : 'Send Message'}
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
